/**
 * Server-side analytics tracking utility.
 * Sends events to /api/track — fully independent, fail-silent.
 * Also fires Facebook Pixel client-side events for ad campaign tracking.
 */
import axios from 'axios';
import {
  pixelViewContent, pixelAddToCart, pixelInitiateCheckout, pixelPurchase, pixelSearch,
  pixelCompleteRegistration, pixelLead,
} from '@/utils/fbpixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem('outlet_session_id');
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('outlet_session_id', sid);
  }
  return sid;
}

// Also sync the old visitor_session key for backward compat
function ensureVisitorSession() {
  const sid = getSessionId();
  if (!localStorage.getItem('visitor_session')) {
    localStorage.setItem('visitor_session', sid);
  }
}
ensureVisitorSession();

/**
 * Throttle: prevents an event from firing more than once per interval.
 * Returns a wrapped function that only executes once per `ms` milliseconds.
 */
const _throttleTimers = {};
function throttledTrack(key, ms, event, data) {
  const now = Date.now();
  if (_throttleTimers[key] && now - _throttleTimers[key] < ms) return;
  _throttleTimers[key] = now;
  track(event, data);
}

/**
 * Track a single event. Fire-and-forget. Never blocks UI.
 */
function track(event, data = {}) {
  const sessionId = getSessionId();
  const payload = {
    event,
    session_id: sessionId,
    page: window.location.pathname,
    referrer: document.referrer || null,
    ...data,
  };

  axios.post(`${API}/track`, payload).catch(() => {});
}

// ===================== CONVENIENCE HELPERS =====================

function trackPageView(page) {
  track('page_view', { page: page || window.location.pathname });
}

function trackProductView(product) {
  track('product_view', {
    product_id: product.product_id,
    product_name: product.name,
    product_price: product.price,
  });
  pixelViewContent(product);
}

function trackAddToCart(product, size, color, quantity) {
  track('add_to_cart', {
    product_id: product.product_id,
    product_name: product.name,
    product_price: product.price,
    extra: { size, color, quantity },
  });
  pixelAddToCart(product, size, color, quantity);
}

function trackRemoveFromCart(product_id, name) {
  track('remove_from_cart', {
    product_id,
    product_name: name,
  });
}

function trackCheckoutStarted(cartTotal, itemCount) {
  track('checkout_started', {
    cart_total: cartTotal,
    extra: { item_count: itemCount },
  });
}

function trackPaymentInitiated(method, cartTotal) {
  track('payment_initiated', {
    payment_method: method,
    cart_total: cartTotal,
  });
}

function trackPurchaseCompleted(orderId, total, method, coupon, items = []) {
  track('purchase_completed', {
    order_id: orderId,
    cart_total: total,
    payment_method: method,
    coupon_code: coupon || null,
  });
  pixelPurchase(orderId, items, total);
}

function trackSearch(query) {
  track('search', { search_query: query });
  pixelSearch(query);
}

function trackCouponApplied(code) {
  track('coupon_applied', { coupon_code: code });
}

// ===================== NEW EVENTS =====================

/** Fires when user clicks a category or subcategory filter. Throttle: 2s */
function trackCategoryClick(category, subcategory) {
  throttledTrack(`cat_${category}_${subcategory}`, 2000, 'category_click', {
    extra: { category: category || '', subcategory: subcategory || '' },
  });
}

/** Fires when user clicks "Comprar Agora" or any banner/ad CTA. Throttle: 3s */
function trackBannerClick(bannerName) {
  throttledTrack(`banner_${bannerName}`, 3000, 'banner_click', {
    extra: { banner: bannerName },
  });
}

/** Fires when user opens the cart page or cart drawer */
function trackCartOpened() {
  throttledTrack('cart_opened', 5000, 'cart_opened', {});
}

/** Fires when user clicks on a promotional/ad element. Throttle: 3s */
function trackAdClick(adName, adTarget) {
  throttledTrack(`ad_${adName}`, 3000, 'ad_click', {
    extra: { ad_name: adName, ad_target: adTarget || '' },
  });
}

export {
  track,
  trackPageView,
  trackProductView,
  trackAddToCart,
  trackRemoveFromCart,
  trackCheckoutStarted,
  trackPaymentInitiated,
  trackPurchaseCompleted,
  trackSearch,
  trackCouponApplied,
  trackCategoryClick,
  trackBannerClick,
  trackCartOpened,
  trackAdClick,
  getSessionId,
  pixelCompleteRegistration,
  pixelLead,
};
